/**
 * Single-session enforcement, client side.
 *
 * One live session per platform per user. This module owns three things:
 * who this device says it is (headers on every request), the 60-second poll
 * that discovers an eviction the WS frame missed, and the decision to act on
 * a `force_logout` frame.
 *
 * The poll — not the frame — is the guarantee. The frame is best-effort and
 * is missed whenever Redis is down, the socket had dropped, or the app was
 * closed at the moment of eviction.
 */
import { isElectron, getDesktopDeviceId } from "./electron";

const TEAMLIO_AUTH_URL =
  process.env.REACT_APP_TEAMLIO_AUTH_URL || "http://localhost:8000";

const DEVICE_ID_KEY = "teamlio.session.deviceId.v1";
const EVICTION_KEY = "teamlio.session.evictedBy";
const HEARTBEAT_MS = 60_000;

/** Web and desktop are separate slots, so they must report separate platforms. */
export const SESSION_PLATFORM: "messenger_desktop" | "messenger_web" = isElectron
  ? "messenger_desktop"
  : "messenger_web";

let cachedDeviceId: string | null = null;
// Held only when storage is blocked (see the catch below) — a random id for
// the life of this page, never written anywhere.
let noStorageDeviceId: string | null = null;

function webDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const generated =
      globalThis.crypto?.randomUUID?.() ??
      `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, generated);
    return generated;
  } catch {
    // Private mode with storage blocked. A per-tab id is wrong (it would
    // evict on every reload), but a fixed string across users would be much
    // worse: every storage-blocked browser on every machine would present
    // the same id, so the backend would treat all of them as one device and
    // never evict between two different physical machines — silently
    // defeating single-session enforcement for exactly these users.
    //
    // Generate a random id once and hold it in memory for this page's
    // lifetime instead. Trade-off: a reload gets a fresh id and evicts the
    // previous one. That's the correct failure mode — worse than a normal
    // persisted device id, but strictly better than two machines silently
    // sharing a slot — so don't "fix" it by reaching for a constant again.
    if (!noStorageDeviceId) {
      noStorageDeviceId =
        globalThis.crypto?.randomUUID?.() ??
        `web-nostorage-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
    return noStorageDeviceId;
  }
}

/**
 * This installation's stable device id.
 *
 * On desktop it comes from electron-store, so it survives reinstalls and the
 * bundle renames. In a browser it lives in localStorage, which means two
 * tabs share one id (correct: one browser is one device) while Chrome and
 * Firefox are two devices (also correct — one of them wins the slot).
 */
export async function getSessionDeviceId(): Promise<string> {
  if (cachedDeviceId) return cachedDeviceId;
  cachedDeviceId = (await getDesktopDeviceId()) ?? webDeviceId();
  return cachedDeviceId;
}

/** A human-readable name for "you were signed out by ..." on the other device. */
export function getSessionDeviceName(): string {
  if (isElectron) {
    const os =
      navigator.platform || navigator.userAgent.slice(0, 40) || "Desktop";
    return `TeamLio Desktop (${os})`;
  }
  const ua = navigator.userAgent;
  const browser = /Firefox\//.test(ua)
    ? "Firefox"
    : /Edg\//.test(ua)
      ? "Edge"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  return `${browser} on ${navigator.platform || "web"}`;
}

/**
 * Headers that let the backend tell this client apart from every other one.
 *
 * No X-Client-Version: REACT_APP_VERSION is set by neither app's build, so
 * the header was always "" and the backend's client_version column was
 * permanently blank. Dropped rather than wired up, to avoid touching the
 * release build scripts of two apps for a column nothing reads yet — if a
 * real need for it shows up, wire REACT_APP_VERSION from package.json in
 * both repos' build scripts and re-add this line to both session.ts copies.
 */
export async function sessionHeaders(): Promise<Record<string, string>> {
  return {
    "X-Client-Platform": SESSION_PLATFORM,
    "X-Client-Device-Id": await getSessionDeviceId(),
    "X-Client-Device-Name": getSessionDeviceName(),
    "X-Client-Os": navigator.platform || "",
  };
}

/**
 * Should this client sign itself out because of `frame`?
 *
 * Pure, and the only place the rule lives:
 *  - the frame must be for MY platform (taking the web slot must not kill the
 *    desktop app), and
 *  - it must not name MY device (that is my own fresh login racing my old
 *    socket, not an eviction).
 */
export function shouldForceLogout(
  frame: unknown,
  me: { platform: string; deviceId: string },
): boolean {
  if (!frame || typeof frame !== "object") return false;
  const f = frame as Record<string, unknown>;
  if (f.type !== "force_logout") return false;
  if (typeof f.platform !== "string" || f.platform !== me.platform) return false;
  // A malformed frame with no device_id must never evict — without this,
  // `undefined !== me.deviceId` would read as "some other device" and sign
  // the user out on the strength of a broken frame.
  if (typeof f.device_id !== "string" || f.device_id.length === 0) return false;
  return f.device_id !== me.deviceId;
}

/**
 * Sign out, loudly.
 *
 * `message` MUST already be a complete, ready-to-render sentence — the login
 * screen displays it verbatim with no wrapping. This is the second time this
 * exact bug has shipped (a bare device name stored here, then wrapped again
 * by the login screen, produced doubled-up garbage like "...used on Signed
 * in on another device (Chrome on MacIntel)."): the WS eviction frame really
 * does carry a bare device_name, so ITS call site is the one that must build
 * the sentence before calling forceLogout; the heartbeat's 409 `detail` is
 * already a full sentence from the backend and must be passed straight
 * through, untouched. Never reintroduce a "device name" parameter here.
 *
 * The message is stashed in sessionStorage so the login screen can explain
 * itself. A silent forced logout is indistinguishable from a bug — which is
 * exactly what made the v1.10.0 bundle rename painful.
 */
export function forceLogout(message?: string | null): void {
  try {
    sessionStorage.setItem(
      EVICTION_KEY,
      message || "You were signed out because your account was used on another device.",
    );
  } catch {
    /* nothing to explain with — still sign out */
  }
  try {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  } catch {
    /* ignore storage errors */
  }
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

/** The complete eviction message for the login screen to render verbatim. Reads once. */
export function takeEvictionMessage(): string | null {
  try {
    const value = sessionStorage.getItem(EVICTION_KEY);
    if (value) sessionStorage.removeItem(EVICTION_KEY);
    return value;
  } catch {
    return null;
  }
}

/**
 * Poll every 60s; a 409 means another device owns this slot now.
 *
 * Returns a cleanup function. Errors other than 409 are ignored on purpose:
 * a flaky network must not sign anyone out.
 */
export function startSessionHeartbeat(): () => void {
  let stopped = false;

  const beat = async () => {
    if (stopped) return;
    let token: string | null = null;
    try {
      token = localStorage.getItem("accessToken");
    } catch {
      return;
    }
    if (!token) return;

    try {
      const res = await fetch(`${TEAMLIO_AUTH_URL}/api/auth/session/heartbeat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          platform: SESSION_PLATFORM,
          device_id: await getSessionDeviceId(),
        }),
      });
      if (res.status === 409 && !stopped) {
        const body = await res.json().catch(() => ({}));
        forceLogout(typeof body?.detail === "string" ? body.detail : null);
      }
    } catch {
      /* offline or the API is down — never sign out over a network blip */
    }
  };

  void beat();
  const timer = setInterval(() => void beat(), HEARTBEAT_MS);
  return () => {
    stopped = true;
    clearInterval(timer);
  };
}
